/**
 * OrgStructure MX — Módulo de Inteligencia Estructural
 * v1.0.0
 * 
 * Dashboard estratégico interno que analiza EXCLUSIVAMENTE los datos
 * de los módulos de Planeación y Seguimiento de la Aplicación de
 * Gobernanza COPARMEX.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Building2, Search, Shield, Lock, TrendingUp, AlertTriangle,
  CheckCircle2, AlertCircle, Activity, Layers, FileText, PieChart as PieChartIcon,
  BarChart3, Target, Info, Eye, ChevronDown, ChevronRight, RefreshCw,
  Briefcase, Users, Lightbulb, X
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar, AreaChart, Area
} from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { WelcomeOnboarding, EmptyState } from './ui/UXComponents';
import type { User } from '../lib/supabase-service';
import {
  getOrgStructureAnalysis,
  checkModuleAccess,
  type OrgStructureResult,
  type OrgCounts,
  type VPSummary,
  type InstanceDetail,
  type StrategicInsight,
} from '../lib/orgstructure-service';

// ─── Constantes de Diseño ───────────────────────────────
const COLORS = {
  emerald: '#10b981',
  cyan: '#06b6d4',
  indigo: '#6366f1',
  amber: '#f59e0b',
  red: '#ef4444',
  violet: '#8b5cf6',
  rose: '#f43f5e',
  sky: '#0ea5e9',
};
const PIE_COLORS = [COLORS.emerald, COLORS.cyan, COLORS.indigo, COLORS.amber];
const KPI_COLORS = [COLORS.emerald, COLORS.cyan, COLORS.indigo, COLORS.violet];

const INSIGHT_STYLES: Record<string, { bg: string; border: string; icon: string; text: string }> = {
  warning: { bg: 'bg-amber-500/5', border: 'border-amber-500/20', icon: 'text-amber-500', text: 'text-amber-400' },
  success: { bg: 'bg-emerald-500/5', border: 'border-emerald-500/20', icon: 'text-emerald-500', text: 'text-emerald-400' },
  info: { bg: 'bg-cyan-500/5', border: 'border-cyan-500/20', icon: 'text-cyan-500', text: 'text-cyan-400' },
  action: { bg: 'bg-rose-500/5', border: 'border-rose-500/20', icon: 'text-rose-500', text: 'text-rose-400' },
};

const ICON_MAP: Record<string, React.ElementType> = {
  PieChart: PieChartIcon,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  AlertCircle,
  FileText,
  Activity,
  Layers,
  Lightbulb,
};

// ─── Sub-Componentes ────────────────────────────────────

/** Tarjeta KPI grande con animación */
const KPICard = ({ label, value, icon: Icon, color, delay, subtitle }: {
  label: string; value: number | string; icon: React.ElementType; color: string; delay: number; subtitle?: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ delay, duration: 0.5, ease: 'easeOut' }}
    className="relative overflow-hidden rounded-[1.5rem] border border-white/5 bg-gradient-to-br from-slate-900/80 to-slate-950/90 backdrop-blur-xl p-6 md:p-8 group hover:border-white/10 transition-all duration-500"
  >
    {/* Glow effect */}
    <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-10 blur-3xl group-hover:opacity-20 transition-opacity duration-700"
      style={{ backgroundColor: color }} />
    
    <div className="flex items-start justify-between">
      <div className="space-y-2">
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40">{label}</p>
        <p className="text-4xl md:text-5xl font-black tracking-tighter" style={{ color }}>{value}</p>
        {subtitle && <p className="text-[11px] text-white/30 font-medium">{subtitle}</p>}
      </div>
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
        style={{ backgroundColor: `${color}15` }}>
        <Icon size={22} style={{ color }} />
      </div>
    </div>
  </motion.div>
);

/** Tarjeta de Insight Estratégico */
const InsightCard = ({ insight, delay, key }: { insight: StrategicInsight; delay: number; key?: string | number }) => {
  const style = INSIGHT_STYLES[insight.type] || INSIGHT_STYLES.info;
  const IconComp = ICON_MAP[insight.icon] || Info;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.4 }}
      className={`rounded-2xl border ${style.border} ${style.bg} p-5 md:p-6 hover:bg-white/5 transition-all duration-300`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${style.bg}`}>
          <IconComp size={18} className={style.icon} />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <h4 className={`font-black text-sm ${style.text}`}>{insight.title}</h4>
            {insight.metric && (
              <span className="text-[10px] font-black bg-white/5 px-3 py-1 rounded-full text-white/60 whitespace-nowrap">
                {insight.metric}
              </span>
            )}
          </div>
          <p className="text-white/60 text-xs leading-relaxed">{insight.description}</p>
        </div>
      </div>
    </motion.div>
  );
};

/** Badge de estado del plan */
const PlanBadge = ({ status }: { status: string }) => {
  const styles: Record<string, string> = {
    'APROBADO': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'BORRADOR': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    'EN REVISIÓN': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    'REVISADO': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    'SIN PLAN': 'bg-red-500/20 text-red-400 border-red-500/30',
    '🔒 Restringido': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  };
  return (
    <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-lg border ${styles[status] || 'bg-slate-800 text-white/40 border-slate-700'}`}>
      {status}
    </span>
  );
};

/** Pantalla de acceso denegado */
const AccessDenied = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.6 }}
    className="flex flex-col items-center justify-center min-h-[60vh] space-y-8 px-4"
  >
    <div className="w-24 h-24 rounded-full bg-red-500/10 flex items-center justify-center">
      <Lock size={40} className="text-red-500/60" />
    </div>
    <div className="text-center space-y-3 max-w-md">
      <h2 className="text-2xl font-black text-white uppercase tracking-tight">Acceso Restringido</h2>
      <p className="text-white/50 text-base leading-relaxed">
        No tengo datos disponibles dentro de tu ámbito de acceso actual.
      </p>
      <p className="text-[10px] text-white/30 uppercase tracking-widest font-bold">
        Se requiere acceso a los módulos de Planeación y Seguimiento
      </p>
    </div>
  </motion.div>
);

/** Tooltip customizado para gráficos */
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900/95 border border-white/10 rounded-xl p-3 shadow-2xl backdrop-blur-xl">
      <p className="text-[10px] font-black text-white/50 uppercase tracking-widest mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} className="text-sm font-bold" style={{ color: p.color }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

// ─── Componente Principal ───────────────────────────────

  const OrgStructureModule = ({ user }: { user: User }) => {
  const [result, setResult] = useState<OrgStructureResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [expandedVP, setExpandedVP] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'instances' | 'insights'>('overview');
  const [isChartReady, setIsChartReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsChartReady(true), 150);
    return () => clearTimeout(timer);
  }, []);

  // Debounce de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Carga inicial y en cambio de query
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getOrgStructureAnalysis(user, debouncedQuery || undefined);
      setResult(data);
      setLoadError(null);
    } catch (err: unknown) {
      console.error('[OrgStructure] Error:', err);
      setLoadError(err instanceof Error ? err.message : 'No fue posible recuperar Planeacion y Seguimiento.');
    } finally {
      setIsLoading(false);
    }
  }, [user, debouncedQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Datos derivados para gráficos ──
  const pieData = useMemo(() => {
    if (!result?.counts) return [];
    return [
      { name: 'Comisiones', value: result.counts.comisiones, color: PIE_COLORS[0] },
      { name: 'Comités', value: result.counts.comites, color: PIE_COLORS[1] },
      { name: 'Consejerías', value: result.counts.consejerias, color: PIE_COLORS[2] },
    ].filter(d => d.value > 0);
  }, [result?.counts]);

  const barData = useMemo(() => {
    if (!result?.vpSummaries) return [];
    return result.vpSummaries.map(vp => ({
      name: vp.name.replace('Vicepresidencia de ', '').replace('Vicepresidencia ', '').substring(0, 20),
      fullName: vp.name,
      Cumplimiento: vp.pct_cumplimiento,
      Instancias: vp.total_instancias,
    }));
  }, [result?.vpSummaries]);

  const radarData = useMemo(() => {
    if (!result?.vpSummaries || result.vpSummaries.length === 0) return [];
    return result.vpSummaries.slice(0, 6).map(vp => ({
      subject: vp.name.replace('Vicepresidencia de ', '').replace('Vicepresidencia ', '').substring(0, 15),
      cumplimiento: vp.pct_cumplimiento,
      instancias: vp.total_instancias * 10, // Escalar para visualización
      planes: vp.approved_plans * 20,
    }));
  }, [result?.vpSummaries]);

  // ── Skeleton Loader ──
  if (isLoading && !result) {
    return (
      <div className="space-y-6 p-4 md:p-8 animate-pulse">
        <div className="h-12 bg-slate-800/50 rounded-2xl w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-36 bg-slate-800/30 rounded-[1.5rem]" />
          ))}
        </div>
        <div className="h-64 bg-slate-800/20 rounded-3xl" />
      </div>
    );
  }

  if (loadError && !result) {
    return (
      <div className="p-8 md:p-12 flex items-center justify-center">
        <div className="max-w-xl w-full p-8 rounded-3xl border border-red-500/30 bg-red-500/10 text-center space-y-4">
          <AlertTriangle className="mx-auto text-red-400" size={36} />
          <h2 className="text-red-200 font-black uppercase tracking-widest">Fallo de lectura institucional</h2>
          <p className="text-white/70 text-sm">{loadError}</p>
          <p className="text-white/40 text-xs">No se generaron SIN PLAN, N/A ni conteos cero a partir del error.</p>
          <button onClick={loadData} className="px-5 py-2 rounded-xl bg-red-500 text-white text-xs font-black uppercase tracking-widest">Reintentar</button>
        </div>
      </div>
    );
  }

  // ── Sin acceso ──
  if (result && !result.hasAccess) {
    return <AccessDenied />;
  }

  return (
    <div className="space-y-6 md:space-y-8 p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
      {loadError && (
        <div className="p-4 rounded-2xl border border-red-500/30 bg-red-500/10 text-red-100 text-sm font-bold">
          {loadError} Se conservan los ultimos datos visibles.
        </div>
      )}
      <WelcomeOnboarding user={user} onDismiss={() => {}} />
      {/* ── Header ── */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center justify-between gap-4"
      >
        <div className="space-y-1">
          <h1 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tighter flex items-center gap-3">
            <Building2 size={28} className="text-emerald-500" />
            OrgStructure <span className="text-emerald-500">MX</span>
          </h1>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/30">
            Inteligencia Estructural • Solo datos internos COPARMEX
          </p>
        </div>

        {/* Badge de acceso */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-2">
            <Shield size={14} className="text-emerald-500" />
            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400">
              {result?.accessScope || 'LOADING'}
            </span>
          </div>
          <button
            onClick={loadData}
            disabled={isLoading}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white/60 hover:text-white rounded-xl px-4 py-2 transition-all text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>
      </motion.div>

      {/* ── Mensaje de acceso ── */}
      {result?.accessMessage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-slate-800/30 border border-white/5 rounded-xl px-4 py-3 flex items-center gap-3"
        >
          <Eye size={14} className="text-white/30 flex-shrink-0" />
          <span className="text-[11px] text-white/40 font-medium">{result.accessMessage}</span>
          {result.restrictedSections.length > 0 && (
            <span className="text-[9px] bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-0.5 rounded-lg font-black uppercase tracking-wider ml-auto flex-shrink-0">
              🔒 {result.restrictedSections.join(', ')}
            </span>
          )}
        </motion.div>
      )}

      {/* ── Barra de Búsqueda ── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="relative"
      >
        <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-white/20" />
        <input
          type="text"
          placeholder="Buscar instancia dentro de COPARMEX..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full bg-slate-900/50 border border-white/5 rounded-2xl pl-14 pr-12 py-4 text-white placeholder:text-white/15 focus:outline-none focus:border-emerald-500/30 focus:ring-1 focus:ring-emerald-500/10 transition-all text-sm font-medium"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-5 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/60 transition-colors"
          >
            <X size={16} />
          </button>
        )}
      </motion.div>

      {/* ── KPI Cards ── */}
      {result?.counts && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <KPICard
            label="Vicepresidencias"
            value={result.counts.vicepresidencias}
            icon={Briefcase}
            color={KPI_COLORS[0]}
            delay={0.15}
            subtitle="Áreas supervisoras"
          />
          <KPICard
            label="Comisiones"
            value={result.counts.comisiones}
            icon={Users}
            color={KPI_COLORS[1]}
            delay={0.2}
            subtitle="Instancias principales"
          />
          <KPICard
            label="Comités"
            value={result.counts.comites}
            icon={Target}
            color={KPI_COLORS[2]}
            delay={0.25}
            subtitle="Trabajo técnico"
          />
          <KPICard
            label="Consejerías"
            value={result.counts.consejerias}
            icon={Shield}
            color={KPI_COLORS[3]}
            delay={0.3}
            subtitle="Asesoría especializada"
          />
        </div>
      )}

      {/* ── Tabs de Navegación ── */}
      <div className="flex gap-1 bg-slate-900/30 rounded-2xl p-1 border border-white/5 overflow-x-auto scrollbar-none">
        {([
          { id: 'overview' as const, label: 'Panorama General', icon: BarChart3 },
          { id: 'instances' as const, label: 'Detalle por Instancia', icon: Building2 },
          { id: 'insights' as const, label: 'Insights Estratégicos', icon: Lightbulb },
        ]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap flex-shrink-0 ${
              activeTab === tab.id
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20'
                : 'text-white/30 hover:text-white/60 hover:bg-white/5'
            }`}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Panorama General ── */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {/* Gráfico: Distribución por Tipo */}
              {pieData.length > 0 && (
                <div className="bg-slate-900/30 border border-white/5 rounded-3xl p-6 md:p-8">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40 mb-6">
                    Distribución por Tipo de Instancia
                  </h3>
                  {isChartReady && (
                   <ResponsiveContainer width="100%" height={280}>
                     <PieChart>
                       <Pie
                         data={pieData}
                         cx="50%"
                         cy="50%"
                         innerRadius={60}
                         outerRadius={100}
                         paddingAngle={4}
                         dataKey="value"
                         strokeWidth={0}
                       >
                         {pieData.map((entry, index) => (
                           <Cell key={`cell-${index}`} fill={entry.color} />
                         ))}
                       </Pie>
                       <Tooltip content={<CustomTooltip />} />
                       <Legend
                         formatter={(value: string) => (
                           <span className="text-[10px] font-bold text-white/50 uppercase tracking-wider">{value}</span>
                         )}
                       />
                     </PieChart>
                   </ResponsiveContainer>
                  )}
                </div>
              )}

              {/* Gráfico: Cumplimiento por VP */}
              {barData.length > 0 && (
                <div className="bg-slate-900/30 border border-white/5 rounded-3xl p-6 md:p-8">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40 mb-6">
                    % Cumplimiento por Vicepresidencia
                  </h3>
                  {isChartReady && (
                   <ResponsiveContainer width="100%" height={280}>
                     <BarChart data={barData} layout="vertical" barCategoryGap={8}>
                       <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
                       <XAxis type="number" domain={[0, 100]} tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} />
                       <YAxis
                         type="category"
                         dataKey="name"
                         width={130}
                         tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }}
                       />
                       <Tooltip content={<CustomTooltip />} />
                       <Bar dataKey="Cumplimiento" fill={COLORS.emerald} radius={[0, 8, 8, 0]} />
                     </BarChart>
                   </ResponsiveContainer>
                  )}
                </div>
              )}
            </div>

            {/* Gráfico Radar (si hay datos suficientes) */}
            {radarData.length >= 3 && (
              <div className="bg-slate-900/30 border border-white/5 rounded-3xl p-6 md:p-8">
                <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40 mb-6">
                  Perfil Multidimensional por VP
                </h3>
                {isChartReady && (
                 <ResponsiveContainer width="100%" height={320}>
                   <RadarChart data={radarData}>
                     <PolarGrid stroke="rgba(255,255,255,0.05)" />
                     <PolarAngleAxis dataKey="subject" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                     <PolarRadiusAxis tick={{ fill: 'rgba(255,255,255,0.2)', fontSize: 9 }} />
                     <Radar
                       name="Cumplimiento %"
                       dataKey="cumplimiento"
                       stroke={COLORS.emerald}
                       fill={COLORS.emerald}
                       fillOpacity={0.15}
                       strokeWidth={2}
                     />
                     <Radar
                       name="Instancias (×10)"
                       dataKey="instancias"
                       stroke={COLORS.cyan}
                       fill={COLORS.cyan}
                       fillOpacity={0.1}
                       strokeWidth={2}
                     />
                     <Tooltip content={<CustomTooltip />} />
                     <Legend
                       formatter={(value: string) => (
                         <span className="text-[10px] font-bold text-white/50">{value}</span>
                       )}
                     />
                   </RadarChart>
                 </ResponsiveContainer>
                )}
              </div>
            )}

            {/* Resumen por VP */}
            {result?.vpSummaries && result.vpSummaries.length > 0 && (
              <div className="bg-slate-900/30 border border-white/5 rounded-3xl overflow-hidden">
                <div className="p-6 border-b border-white/5">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40">
                    Resumen por Vicepresidencia
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead>
                      <tr className="text-[9px] font-black uppercase tracking-[0.2em] text-white/30 border-b border-white/5">
                        <th className="text-left px-6 py-4">Vicepresidencia</th>
                        <th className="text-center px-3 py-4">Comisiones</th>
                        <th className="text-center px-3 py-4">Comités</th>
                        <th className="text-center px-3 py-4">Consejerías</th>
                        <th className="text-center px-3 py-4">% Cumplimiento</th>
                        <th className="text-center px-3 py-4">Planes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {result.vpSummaries.map((vp, i) => (
                        <tr key={vp.id} className="hover:bg-white/[0.02] transition-colors">
                          <td className="px-6 py-4">
                            <p className="font-bold text-white text-sm">{vp.name}</p>
                            <p className="text-[10px] text-white/30 mt-0.5">{vp.president_name}</p>
                          </td>
                          <td className="text-center px-3 py-4">
                            <span className="text-emerald-400 font-black text-lg">{vp.comisiones}</span>
                          </td>
                          <td className="text-center px-3 py-4">
                            <span className="text-cyan-400 font-black text-lg">{vp.comites}</span>
                          </td>
                          <td className="text-center px-3 py-4">
                            <span className="text-indigo-400 font-black text-lg">{vp.consejerias}</span>
                          </td>
                          <td className="text-center px-3 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 h-2 bg-slate-800 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{
                                    width: `${Math.min(vp.pct_cumplimiento, 100)}%`,
                                    backgroundColor: vp.pct_cumplimiento >= 80 ? COLORS.emerald :
                                      vp.pct_cumplimiento >= 50 ? COLORS.amber : COLORS.red,
                                  }}
                                />
                              </div>
                              <span className="text-white/60 font-bold text-xs">{vp.pct_cumplimiento}%</span>
                            </div>
                          </td>
                          <td className="text-center px-3 py-4">
                            <span className="text-[10px] font-bold">
                              <span className="text-emerald-400">{vp.approved_plans}✓</span>
                              {vp.pending_plans > 0 && <span className="text-amber-400 ml-1">{vp.pending_plans}⏳</span>}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* ── Tab: Detalle por Instancia ── */}
        {activeTab === 'instances' && (
          <motion.div
            key="instances"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            {result?.instances && result.instances.length > 0 ? (
              result.instances.map((inst, idx) => (
                <motion.div
                  key={inst.id}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className="bg-slate-900/30 border border-white/5 rounded-2xl p-4 md:p-5 hover:bg-white/[0.02] transition-all"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-white text-sm truncate">{inst.name}</h4>
                        <span className={`text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded-lg border ${
                          inst.type === 'COMISION' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                          inst.type === 'COMITE' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' :
                          'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                        }`}>
                          {inst.type === 'COMISION' ? 'Comisión' : inst.type === 'COMITE' ? 'Comité' : 'Consejería'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-white/30 font-medium flex-wrap">
                        <span>VP: {inst.vp_name}</span>
                        <span>•</span>
                        <span>Pdte: {inst.president_name}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <PlanBadge status={inst.plan_status} />
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${Math.min(inst.pct_cumplimiento, 100)}%`,
                              backgroundColor: inst.pct_cumplimiento >= 80 ? COLORS.emerald :
                                inst.pct_cumplimiento >= 50 ? COLORS.amber : COLORS.red,
                            }}
                          />
                        </div>
                        <span className="text-xs font-bold text-white/50 w-10 text-right">{inst.pct_cumplimiento}%</span>
                      </div>
                      {inst.total_indicators > 0 && (
                        <span className="text-[9px] font-bold text-white/20 flex items-center gap-1">
                          <Activity size={10} /> {inst.total_indicators} KPIs
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Mini semáforo de KPIs */}
                  {(inst.kpi_green + inst.kpi_amber + inst.kpi_red) > 0 && (
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-[8px] font-black uppercase tracking-wider text-white/20">Semáforo:</span>
                      <div className="flex items-center gap-1">
                        {inst.kpi_green > 0 && <span className="w-5 h-5 rounded-lg bg-emerald-500/20 text-emerald-400 text-[9px] font-black flex items-center justify-center">{inst.kpi_green}</span>}
                        {inst.kpi_amber > 0 && <span className="w-5 h-5 rounded-lg bg-amber-500/20 text-amber-400 text-[9px] font-black flex items-center justify-center">{inst.kpi_amber}</span>}
                        {inst.kpi_red > 0 && <span className="w-5 h-5 rounded-lg bg-red-500/20 text-red-400 text-[9px] font-black flex items-center justify-center">{inst.kpi_red}</span>}
                      </div>
                    </div>
                  )}
                </motion.div>
              ))
            ) : (
              <div className="text-center py-20">
                <EmptyState 
                  title="Sin Resultados"
                  description={searchQuery ? `No se encontraron instancias para "${searchQuery}"` : "No hay instancias registradas en esta vista."}
                  icon={Building2}
                />
              </div>
            )}
          </motion.div>
        )}

        {/* ── Tab: Insights Estratégicos ── */}
        {activeTab === 'insights' && (
          <motion.div
            key="insights"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="bg-indigo-500/5 border border-indigo-500/10 rounded-2xl p-4 flex items-center gap-3">
              <Info size={16} className="text-indigo-400 flex-shrink-0" />
              <p className="text-[11px] text-indigo-300/70 font-medium">
                Estos insights se generan exclusivamente a partir de los datos, indicadores y gráficos disponibles en los módulos de Planeación y Seguimiento de COPARMEX. No se utilizan fuentes externas.
              </p>
            </div>

            {result?.insights && result.insights.length > 0 ? (
              result.insights.map((insight, idx) => (
                <InsightCard key={idx} insight={insight} delay={idx * 0.08} />
              ))
            ) : (
              <div className="text-center py-20">
                <EmptyState 
                  title="Sin Insights"
                  description="No hay suficientes datos para generar análisis estructural en este momento. Se requieren al menos 2 instancias con datos."
                  icon={Lightbulb}
                />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Footer: Fuente de Datos ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center py-4 space-y-1"
      >
        <p className="text-[9px] font-black uppercase tracking-[0.4em] text-white/10">
          Fuente: Módulos de Planeación y Seguimiento • Gobernanza COPARMEX
        </p>
        <p className="text-[8px] text-white/8">
          Actualizado: {result?.timestamp ? new Date(result.timestamp).toLocaleString('es-MX') : '—'}
        </p>
      </motion.div>
    </div>
  );
};

export default OrgStructureModule;
